import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import {
  sanitizeTextInput,
  sanitizeSearchInput,
  sanitizeAddressInput,
  sanitizeDescriptionInput,
  validateNumberInput,
  validatePhoneInput,
  validateEmailInput,
  getCharacterCount,
  FIELD_LIMITS,
  NUMBER_LIMITS,
} from '../../../utils/inputValidation';
import PermissionGate from '../../../components/common/PermissionGate';
import { API_ENDPOINTS } from '../../../api/config';
import tenderService from '../../../api/crm/tenders/tenderService';
import { toast } from 'react-hot-toast';
import SearchableDropdown from '../../../components/common/SearchableDropdown';
import { useAuth } from '../../../context/AuthContext';
import { USER_ROLES } from '../../../utils/constants';


const TenderForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSalesExecutive = user?.role === USER_ROLES.SALES_EXECUTIVE || user?.role === USER_ROLES.EXPORT_EXECUTIVE || user?.role === USER_ROLES.BUSINESS_RELATIONSHIP_MANAGER || user?.role === USER_ROLES.BUSINESS_DEVELOPMENT_EXECUTIVE;
  const currentUserId = user?.id || user?._id || '';
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [form, setForm] = useState({
    title: '',
    organization: '',
    tenderValue: '',
    submissionDate: '',
    openingDate: '',
    location: '',
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    description: '',
    priority: 'Medium',
    assignedTo: '',
    status: 'Open',
  });

  useEffect(() => {
    const fetchTender = async () => {
      if (!isEdit) return;
      try {
        const result = await tenderService.getTenderById(id);
        if (result.success && result.data) {
          const t = result.data;
          setForm({
            title: t.title || '',
            organization: t.organization || '',
            tenderValue: t.tenderValue || '',
            submissionDate: t.submissionDate ? t.submissionDate.substring(0, 10) : '',
            openingDate: t.openingDate ? t.openingDate.substring(0, 10) : '',
            location: t.location || '',
            contactPerson: t.contactPerson || '',
            contactEmail: t.contactEmail || '',
            contactPhone: t.contactPhone || '',
            description: t.description || '',
            priority: t.priority || 'Medium',
            assignedTo: (t.assignedTo && typeof t.assignedTo === 'object') ? (t.assignedTo._id || t.assignedTo.id) : (t.assignedTo || ''),
            status: t.status || 'Open',
          });
        } else {
          toast.error(result.error || 'Failed to load tender');
          navigate('/tender_lead');
        }
      } catch (error) {
        toast.error('An error occurred while loading tender');
        navigate('/tender_lead');
      } finally {
        setLoading(false);
      }
    };

    fetchTender();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  // Load CRM team members for assignment (same API used in lead forms)
  useEffect(() => {
    const fetchUsers = async () => {
      if (isSalesExecutive) {
        setUsers([]);
        setLoadingUsers(false);
        return;
      }

      setLoadingUsers(true);
      try {
        // Add roleFilter=sales to fetch only Sales Manager and Sales Executive users
        const params = new URLSearchParams();
        params.append('roleFilter', 'sales');

        // If user is a sales manager or export manager, filter subordinates only
        if (user?.role === USER_ROLES.SALES_MANAGER || user?.role === USER_ROLES.EXPORT_MANAGER) {
          params.append('managerId', user._id || user.id);
        }

        const url = `${API_ENDPOINTS.CRM.TEAM_MANAGEMENT.USERS}?${params.toString()}`;
        const response = await authFetch(url, {
          credentials: 'include',
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const result = await response.json();
        if (result.responseCode === 200) {
          const crmUsers = result.data?.users || [];
          const transformed = crmUsers
            .map(user => {
              const name = user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Unknown';
              const email = user.email || '';
              const designation = user.designation || '';
              return {
                id: user.user || user._id || user.id,
                name: name,
                email: email,
                designation: designation,
                displayText: `${name}${email ? ` (${email})` : ''}${designation ? ` - ${designation}` : ''}`,
                searchText: `${name} ${email} ${designation}`.toLowerCase()
              };
            })
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          setUsers(transformed);
        } else {
          setUsers([]);
        }
      } catch (error) {
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [isSalesExecutive, user?.role, user?.id, user?._id]);

  useEffect(() => {
    if (!isEdit && isSalesExecutive && currentUserId) {
      setForm(prev => ({
        ...prev,
        assignedTo: currentUserId
      }));
    }
  }, [isEdit, isSalesExecutive, currentUserId]);

  const requiredFields = ['title', 'organization', 'tenderValue', 'submissionDate', 'openingDate'];

  const validateFormData = () => {
    const newErrors = {};

    requiredFields.forEach(field => {
      const value = form[field];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        const fieldLabel = field
          .charAt(0)
          .toUpperCase() +
          field.slice(1).replace(/([A-Z])/g, ' $1').trim();
        newErrors[field] = `${fieldLabel} is required`;
      }
    });

    if (form.contactEmail) {
      const emailValidation = validateEmailInput(form.contactEmail);
      if (!emailValidation.isValid) {
        newErrors.contactEmail = emailValidation.error;
      }
    }

    if (form.contactPhone) {
      const phoneValidation = validatePhoneInput(form.contactPhone);
      if (!phoneValidation.isValid) {
        newErrors.contactPhone = phoneValidation.error || 'Phone number must be 10 digits';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value, fieldType = 'text', maxLength = FIELD_LIMITS[field] || 100) => {
    let processedValue = value;
    let error = '';

    if (fieldType === 'select') {
      processedValue = value || '';
    } else if (fieldType === 'text') {
      processedValue = sanitizeTextInput(String(value || ''));
      processedValue = processedValue.slice(0, maxLength);
    } else if (fieldType === 'number') {
      const maxValue = NUMBER_LIMITS[field]?.max || Infinity;
      const minValue = NUMBER_LIMITS[field]?.min || 0;
      const numValidation = validateNumberInput(value, maxValue, minValue);
      processedValue = numValidation.value;
      error = numValidation.error || '';
    } else if (fieldType === 'phone') {
      const phoneValidation = validatePhoneInput(value);
      processedValue = phoneValidation.value;
      error = phoneValidation.error || '';
    } else if (fieldType === 'email') {
      processedValue = sanitizeSearchInput(String(value || '')).toLowerCase();
      processedValue = processedValue.slice(0, maxLength);
      const emailValidation = validateEmailInput(processedValue);
      if (!emailValidation.isValid && processedValue.length > 0) {
        // Only set error error on change if it's not a complete validation (handled by blur/submit)
        // or just rely on validateFormData and handleBlur
      }
    }
    else if (fieldType === 'textarea') {
      processedValue = sanitizeTextInput(String(value || ''));
      processedValue = processedValue.slice(0, maxLength);
    } else if (fieldType === 'description') {
      processedValue = sanitizeDescriptionInput(String(value || ''));
      processedValue = processedValue.slice(0, maxLength);
    } else if (fieldType === 'address') {
      processedValue = sanitizeAddressInput(String(value || ''));
      processedValue = processedValue.slice(0, maxLength);
    }

    setForm(prev => ({
      ...prev,
      [field]: processedValue,
    }));

    if (error) {
      setErrors(prev => ({
        ...prev,
        [field]: error,
      }));
    } else if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleBlur = (field) => {
    if (requiredFields.includes(field)) {
      const value = form[field];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        const fieldLabel = field === 'tenderValue' ? 'Tender Value' :
          field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1').trim();
        setErrors(prev => ({
          ...prev,
          [field]: `${fieldLabel} is required`
        }));
      }
    } else if (field === 'contactEmail') {
      const value = form[field];
      if (value) {
        const emailValidation = validateEmailInput(value);
        if (!emailValidation.isValid) {
          setErrors(prev => ({
            ...prev,
            [field]: emailValidation.error
          }));
        }
      }
    }
  };

  const handleDateClick = (e) => {
    try {
      if (e.target.showPicker) {
        e.target.showPicker();
      }
    } catch (error) {
      // Validation error or browser not supporting showPicker
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateFormData()) {
      const firstErrorField = Object.keys(errors)[0] || requiredFields.find(f => !form[f]);
      if (firstErrorField) {
        const fieldElements = document.getElementsByName(firstErrorField);
        if (fieldElements.length > 0) {
          fieldElements[0].focus();
        }
      }
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        assignedTo: form.assignedTo && String(form.assignedTo).trim() !== '' ? form.assignedTo : null,
      };

      if (!isEdit && isSalesExecutive && currentUserId) {
        payload.assignedTo = currentUserId;
      }

      let result;
      if (isEdit) {
        result = await tenderService.updateTender(id, payload);
      } else {
        result = await tenderService.createTender(payload);
      }

      if (result.success) {
        toast.success(isEdit ? 'Tender updated successfully' : 'Tender created successfully');
        navigate('/tender_lead');
      } else {
        toast.error(result.error || 'Operation failed');
      }
    } catch (error) {
      toast.error('An error occurred while saving tender');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading tender...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate('/tender_lead')}
            className="flex-shrink-0"
          >
            Back to Tenders
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              {isEdit ? 'Edit Tender' : 'Create Tender'}
            </h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">
              {isEdit ? 'Update tender details' : 'Create a new tender record'}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} noValidate className="space-y-6">
        {/* Basic Information */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tender Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={form.title}
                onChange={(e) => handleInputChange('title', e.target.value, 'text', 100)}
                onBlur={() => handleBlur('title')}
                placeholder="Enter tender title"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.title ? 'border-red-500' : 'border-gray-300'}`}
                maxLength={100}
              />
              {errors.title && (
                <p className="mt-1 text-xs text-red-600">{errors.title}</p>
              )}
              <div className="flex justify-end mt-1">
                <span className="text-xs text-gray-500">
                  {getCharacterCount(form.title, 100)}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Organization <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="organization"
                value={form.organization}
                onChange={(e) =>
                  handleInputChange('organization', e.target.value, 'text', FIELD_LIMITS.company)
                }
                onBlur={() => handleBlur('organization')}
                placeholder="Enter organization name"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.organization ? 'border-red-500' : 'border-gray-300'}`}
                maxLength={FIELD_LIMITS.company}
              />
              {errors.organization && (
                <p className="mt-1 text-xs text-red-600">{errors.organization}</p>
              )}
              <div className="flex justify-end mt-1">
                <span className="text-xs text-gray-500">
                  {getCharacterCount(form.organization, FIELD_LIMITS.company)}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tender Value <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="tenderValue"
                value={form.tenderValue}
                onChange={(e) => handleInputChange('tenderValue', e.target.value, 'number')}
                onBlur={() => handleBlur('tenderValue')}
                placeholder="₹0.00"
                min="0"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.tenderValue ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.tenderValue && (
                <p className="mt-1 text-xs text-red-600">{errors.tenderValue}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                type="text"
                name="location"
                value={form.location}
                onChange={(e) =>
                  handleInputChange('location', e.target.value, 'address', FIELD_LIMITS.city)
                }
                placeholder="Enter location"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                maxLength={FIELD_LIMITS.city}
              />
              <div className="flex justify-end mt-1">
                <span className="text-xs text-gray-500">
                  {getCharacterCount(form.location, FIELD_LIMITS.city)}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                name="priority"
                value={form.priority}
                onChange={(e) => handleInputChange('priority', e.target.value, 'select')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
              >
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                name="status"
                value={form.status}
                onChange={(e) => handleInputChange('status', e.target.value, 'select')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
              >
                <option value="Open">Open</option>
                <option value="Pending">Pending</option>
                <option value="Process">Process</option>
                <option value="Completed">Completed</option>
                <option value="Closed">Closed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>

        {/* Important Dates */}
        <div className="bg-blue-50 rounded-lg p-4">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Important Dates</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Opening Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="openingDate"
                value={form.openingDate}
                onChange={(e) => handleInputChange('openingDate', e.target.value, 'select')}
                onBlur={() => handleBlur('openingDate')}
                onClick={handleDateClick}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.openingDate ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.openingDate && (
                <p className="mt-1 text-xs text-red-600">{errors.openingDate}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Submission Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="submissionDate"
                value={form.submissionDate}
                onChange={(e) => handleInputChange('submissionDate', e.target.value, 'select')}
                onBlur={() => handleBlur('submissionDate')}
                onClick={handleDateClick}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.submissionDate ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.submissionDate && (
                <p className="mt-1 text-xs text-red-600">{errors.submissionDate}</p>
              )}
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-green-50 rounded-lg p-4">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Person
              </label>
              <input
                type="text"
                name="contactPerson"
                value={form.contactPerson}
                onChange={(e) =>
                  handleInputChange('contactPerson', e.target.value, 'text', FIELD_LIMITS.customerName)
                }
                placeholder="Enter contact person name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                maxLength={FIELD_LIMITS.customerName}
              />
              <div className="flex justify-end mt-1">
                <span className="text-xs text-gray-500">
                  {getCharacterCount(form.contactPerson, FIELD_LIMITS.customerName)}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Email
              </label>
              <input
                type="email"
                name="contactEmail"
                value={form.contactEmail}
                onChange={(e) => handleInputChange('contactEmail', e.target.value, 'email', 100)}
                onBlur={() => handleBlur('contactEmail')}
                placeholder="Enter contact email"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.contactEmail ? 'border-red-500' : 'border-gray-300'}`}
                maxLength={100}
              />
              {errors.contactEmail && (
                <p className="mt-1 text-xs text-red-600">{errors.contactEmail}</p>
              )}
              <div className="flex justify-end mt-1">
                <span className="text-xs text-gray-500">
                  {getCharacterCount(form.contactEmail, 100)}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Phone
              </label>
              <input
                type="tel"
                name="contactPhone"
                value={form.contactPhone}
                onChange={(e) => handleInputChange('contactPhone', e.target.value, 'phone')}
                placeholder="Enter contact phone"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                maxLength={15}
              />
              {errors.contactPhone && (
                <p className="mt-1 text-xs text-red-600">{errors.contactPhone}</p>
              )}
              <div className="flex justify-end mt-1">
                <span className="text-xs text-gray-500">
                  {getCharacterCount(form.contactPhone, FIELD_LIMITS.phone)}
                </span>
              </div>
            </div>

            {!isSalesExecutive && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assigned To
                </label>
                <SearchableDropdown
                  options={users}
                  value={form.assignedTo}
                  onChange={(value) => handleInputChange('assignedTo', value, 'select')}
                  placeholder="Select assignee"
                  loading={loadingUsers}
                  className="w-full"
                  searchPlaceholder="Search by name, email or designation..."
                />
                {loadingUsers && (
                  <p className="mt-1 text-xs text-gray-500">Loading team members...</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tender Description
          </label>
          <textarea
            name="description"
            value={form.description}
            onChange={(e) =>
              handleInputChange('description', e.target.value, 'description', FIELD_LIMITS.notes)
            }
            rows={4}
            placeholder="Enter detailed description of the tender..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
            maxLength={FIELD_LIMITS.notes}
          />
          <div className="flex justify-end mt-1">
            <span className="text-xs text-gray-500">
              {getCharacterCount(form.description, FIELD_LIMITS.notes)}
            </span>
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <Button
            variant="outline"
            onClick={() => navigate('/tender_lead')}
            disabled={saving}
          >
            Cancel
          </Button>
          <PermissionGate permission="lead_management" action={isEdit ? "update" : "create"}>
            <Button
              variant="primary"
              type="submit"
              disabled={saving}
              icon={<FileText className="h-4 w-4" />}
            >
              {saving ? (isEdit ? 'Updating...' : 'Creating...') : isEdit ? 'Update Tender' : 'Create Tender'}
            </Button>
          </PermissionGate>
        </div>
      </form >
    </div >
  );
};

export default TenderForm;


